import React, { useState } from 'react';
import { FormContainer } from '../../styled';
import { fetchApiData } from '../../Utils/ApiCalls';
import { FETCH_PLATFORM_ACCESS, UPDATE_PLATFORM_ACCESS } from '../../urls';
import { Button, Input, Checkbox, showToast } from '@attentive-platform/stem-ui';

const USER_TYPE = {
  PRO: 'pro',
  ENTERPRISE: 'enterprise'
};

const USER_TYPE_LABELS = {
  [USER_TYPE.PRO]: 'Pro',
  [USER_TYPE.ENTERPRISE]: 'Enterprise'
};

const UserTypeSelector = ({ userType, onUserTypeChange }) => {
  return (
    <div className='mb-6 flex justify-between items-center'>
      <label className='text-sm font-medium text-gray-700 block'>User type</label>
      <div className='flex items-center'>
        <Checkbox
          checked={userType === USER_TYPE.PRO}
          label={USER_TYPE_LABELS[USER_TYPE.PRO]}
          onChange={() => onUserTypeChange(USER_TYPE.PRO)}
        />
        <Checkbox
          checked={userType === USER_TYPE.ENTERPRISE}
          label={USER_TYPE_LABELS[USER_TYPE.ENTERPRISE]}
          onChange={() => onUserTypeChange(USER_TYPE.ENTERPRISE)}
        />
      </div>
    </div>
  );
};

const EmailInputSection = ({ email, onEmailChange, onProceed, loading, isEmailDisabled, buttonText }) => {
  return (
    <div className='mb-6'>
      <div className='flex gap-2'>
        <Input
          value={email}
          className='w-full'
          placeholder='Enter email'
          disabled={isEmailDisabled}
          onChange={e => onEmailChange('email', e.target.value)}
        />
        <Button color='primary' variant='outlined' onClick={onProceed} loading={loading}>
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

const DateInputsSection = ({ formData, onDataChange, userType }) => {
  const fieldConfigs = {
    [USER_TYPE.ENTERPRISE]: [
      {
        key: 'planStartDate',
        label: 'Contract start date',
        disabled: true
      },
      {
        key: 'planEndDate',
        label: 'Contract end date',
        disabled: !formData.planStartDate
      }
    ],
    [USER_TYPE.PRO]: [
      {
        key: 'planStartDate',
        label: 'Plan start date',
        disabled: true
      },
      {
        key: 'nextBillingDate',
        label: 'Next billing date',
        disabled: true
      },
      {
        key: 'planEndDate',
        label: 'Plan end date',
        disabled: !formData.planStartDate && !formData.nextBillingDate
      }
    ]
  };

  const DateInput = ({ config }) => (
    <div className='flex items-center justify-between'>
      <label className='text-sm font-medium text-gray-700'>{config.label}</label>
      <Input
        type='date'
        size='small'
        className='w-48'
        disabled={config.disabled}
        value={formData[config.key]}
        onChange={e => onDataChange(config.key, e.target.value)}
      />
    </div>
  );

  const currentConfig = fieldConfigs[userType];

  return (
    <div className='space-y-4 mb-6'>
      {currentConfig.map((config, index) => (
        <DateInput key={index} config={config} />
      ))}
    </div>
  );
};

const EnablePlatformAccess = () => {
  const [formData, setFormData] = useState({
    email: '',
    planEndDate: '',
    planStartDate: '',
    nextBillingDate: '',
    userType: USER_TYPE.PRO
  });
  const [loading, setLoading] = useState(false);
  const [accessId, setAccessId] = useState(null);
  const [proceedLoading, setProceedLoading] = useState(false);

  const handleDataChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleUserTypeChange = type => {
    setFormData(prev => ({
      ...prev,
      email: '',
      userType: type,
      planEndDate: '',
      planStartDate: '',
      nextBillingDate: ''
    }));
    setAccessId(null);
  };

  const handleProceed = async () => {
    if (!formData.email) {
      showToast('Please enter an email address', 'error');
      return;
    }

    setProceedLoading(true);

    const data = {
      email: formData.email,
      is_pro: formData.userType === USER_TYPE.PRO
    };

    try {
      const response = await fetchApiData({
        endpoint: FETCH_PLATFORM_ACCESS,
        apiRequest: 'POST',
        data
      });

      if (response.error) {
        showToast(response.error || 'Failed to fetch platform access data', 'error');
        return;
      }

      const { id, start_date, next_billing_date } = response?.data;

      setAccessId(id);
      setFormData(prev => ({
        ...prev,
        planStartDate: start_date || '',
        nextBillingDate: next_billing_date || '',
        planEndDate: ''
      }));

      showToast('Platform access data fetched successfully', 'success');
    } catch (error) {
      showToast('Failed to fetch platform access data', 'error');
    } finally {
      setProceedLoading(false);
    }
  };

  const handleClear = () => {
    setFormData(prev => ({
      ...prev,
      email: '',
      planEndDate: '',
      planStartDate: '',
      nextBillingDate: ''
    }));
    setAccessId(null);
    showToast('Form cleared successfully', 'success');
  };

  const handleUpdate = async () => {
    if (!formData.planEndDate) {
      showToast('Please fill in the end date', 'error');
      return;
    }

    setLoading(true);

    try {
      const response = await fetchApiData({
        endpoint: UPDATE_PLATFORM_ACCESS,
        apiRequest: 'POST',
        data: {
          id: accessId,
          email: formData.email,
          admin_email: formData.email,
          end_date: formData.planEndDate,
          is_pro: formData.userType === USER_TYPE.PRO
        }
      });

      if (response.error) {
        showToast(response.error || 'Failed to update platform access', 'error');
        return;
      }

      showToast('Platform access update initiated successfully', 'success');

      setFormData({
        email: '',
        planEndDate: '',
        planStartDate: '',
        nextBillingDate: '',
        userType: USER_TYPE.PRO
      });
      setAccessId(null);
    } catch (error) {
      showToast('Failed to update platform access', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isEnterprise = formData.userType === USER_TYPE.ENTERPRISE;
  const isUpdateDisabled = isEnterprise
    ? !formData.planStartDate || !formData.planEndDate || !accessId
    : !formData.planStartDate || !formData.nextBillingDate || !formData.planEndDate || !accessId;

  const isEmailDisabled = !!accessId;
  const buttonText = accessId ? 'Clear' : 'Proceed';
  const handleEmailAction = accessId ? handleClear : handleProceed;

  return (
    <FormContainer>
      <div className='border border-gray-200 rounded-lg p-6 bg-white'>
        <UserTypeSelector userType={formData.userType} onUserTypeChange={handleUserTypeChange} />

        <EmailInputSection
          email={formData.email}
          buttonText={buttonText}
          loading={proceedLoading}
          onProceed={handleEmailAction}
          onEmailChange={handleDataChange}
          isEmailDisabled={isEmailDisabled}
        />

        <DateInputsSection formData={formData} userType={formData.userType} onDataChange={handleDataChange} />

        <div className='flex justify-center'>
          <Button color='primary' onClick={handleUpdate} disabled={isUpdateDisabled} loading={loading} className='px-8'>
            Update
          </Button>
        </div>
      </div>
    </FormContainer>
  );
};

export default EnablePlatformAccess;
